import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ImagePlus, Check, Loader2, Trash2, Image as ImageIcon } from 'lucide-react';
import {
  MediaItem,
  MediaItemWithUsage,
  MediaUsageFilter,
  deleteMedia,
  isMediaUsedForConnection,
  isVideoMedia,
  listMediaWithPlatformUsage,
  mediaPreviewUrl,
  uploadMedia,
} from '../lib/mediaLibrary';
import MediaTileMedia from './MediaTileMedia';
import { toast } from './toasts/SimpleToast';

interface MediaLibraryPickerProps {
  isOpen: boolean;
  businessId: string;
  /** Allow selecting more than one item. Default true. */
  multiple?: boolean;
  /** URLs already attached to the target, used to pre-select on open. */
  initialSelectedUrls?: string[];
  /**
   * When set, hide images already used for this platform connection/location.
   * Currently selected URLs are still shown so the user can keep their pick.
   */
  excludeUsedFor?: MediaUsageFilter | null;
  /** Overlay z-index — raise when opened above another modal. */
  zIndex?: number;
  /** Offer video uploads and show existing videos. Default false (images only). */
  allowVideo?: boolean;
  onClose: () => void;
  onDone: (selected: MediaItem[]) => void;
}

export default function MediaLibraryPicker({
  isOpen,
  businessId,
  multiple = true,
  initialSelectedUrls = [],
  excludeUsedFor = null,
  zIndex = 100090,
  allowVideo = false,
  onClose,
  onDone,
}: MediaLibraryPickerProps) {
  const [media, setMedia] = useState<MediaItemWithUsage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen || !businessId) return;
    let cancelled = false;
    setLoading(true);
    setSearch('');
    listMediaWithPlatformUsage(businessId)
      .then((items) => {
        if (cancelled) return;
        setMedia(items);
        const preset = new Set(
          items.filter((i) => initialSelectedUrls.includes(i.url)).map((i) => i.id),
        );
        setSelectedIds(preset);
      })
      .catch((err) => {
        console.error('Failed to load media library:', err);
        toast.error({ title: 'Failed to load media' });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, businessId, excludeUsedFor?.connectionId, excludeUsedFor?.platform]);

  if (!isOpen) return null;

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(multiple ? prev : []);
      if (prev.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // Warm the working-size image so the composer preview is instant.
        const item = media.find((m) => m.id === id);
        if (item) new Image().src = mediaPreviewUrl(item.url);
      }
      return next;
    });
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    try {
      const created = await uploadMedia(businessId, files);
      const withUsage: MediaItemWithUsage[] = created.map((c) => ({
        ...c,
        platformUsage: [],
      }));
      setMedia((prev) => [...withUsage, ...prev]);
      setSelectedIds((prev) => {
        const next = new Set(multiple ? prev : []);
        if (multiple) {
          created.forEach((c) => next.add(c.id));
        } else if (created[0]) {
          next.clear();
          next.add(created[0].id);
        }
        return next;
      });
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item: MediaItem, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteMedia(item.id);
      setMedia((prev) => prev.filter((m) => m.id !== item.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error({ title: 'Failed to delete media' });
    }
  };

  const handleDone = () => {
    const selected = media.filter((m) => selectedIds.has(m.id));
    onDone(selected);
    onClose();
  };

  const available = media.filter((m) => {
    if (!allowVideo && isVideoMedia(m)) return false;
    if (!excludeUsedFor?.connectionId) return true;
    if (initialSelectedUrls.includes(m.url)) return true;
    return !isMediaUsedForConnection(m, excludeUsedFor.connectionId);
  });

  const filtered = available.filter(
    (m) =>
      search.trim() === '' ||
      (m.file_name || '').toLowerCase().includes(search.toLowerCase()),
  );

  const filterHint =
    excludeUsedFor?.platform === 'google'
      ? 'Showing images not yet used on this Google location'
      : excludeUsedFor?.platform === 'facebook'
        ? 'Showing images not yet used on Facebook'
        : excludeUsedFor?.platform === 'instagram'
        ? 'Showing images not yet used on Instagram'
        : excludeUsedFor?.platform === 'linkedin'
          ? 'Showing images not yet used on LinkedIn'
          : null;

  const content = (
    <div
      className="fixed inset-0 bg-white"
      style={{ zIndex }}
    >
      <div
        className="bg-white w-full h-full flex flex-col overflow-hidden"
        // Always full-screen — keep header/footer clear of the iOS notch & home indicator
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">Select file</h2>
            {filterHint ? (
              <p className="text-[11px] text-gray-500 mt-0.5 truncate">{filterHint}</p>
            ) : null}
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files"
              className="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ImagePlus className="h-4 w-4" />
            )}
            Add media
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={allowVideo ? 'image/*,video/mp4,video/quicktime,.mov,.m4v' : 'image/*'}
            multiple={multiple}
            onChange={handleUpload}
            className="hidden"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-5" style={{ WebkitOverflowScrolling: 'touch' }}>
          {loading ? (
            <div className="h-48 flex items-center justify-center text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="h-48 flex flex-col items-center justify-center text-center text-gray-400">
              <ImageIcon className="h-10 w-10 mb-3" />
              <p className="text-sm">
                {search
                  ? 'No files match your search'
                  : excludeUsedFor
                    ? 'No unused images for this channel — upload new media'
                    : 'No media yet'}
              </p>
              {!search && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-700"
                >
                  Upload your first file
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {filtered.map((item) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => toggle(item.id)}
                    className="group cursor-pointer"
                  >
                    <div
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                        isSelected
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <MediaTileMedia
                        url={item.url}
                        mimeType={item.mime_type}
                        durationSeconds={item.duration_seconds}
                        alt={item.file_name || ''}
                      />
                      <div
                        className={`absolute top-1.5 left-1.5 h-5 w-5 rounded-md flex items-center justify-center transition-all ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-white/80 border border-gray-300'
                        }`}
                      >
                        {isSelected && <Check className="h-3.5 w-3.5" />}
                      </div>
                      <button
                        onClick={(e) => handleDelete(item, e)}
                        className="absolute top-1.5 right-1.5 h-6 w-6 rounded-md bg-white/90 text-gray-500 hover:text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-600 truncate">
                      {item.file_name || 'Untitled'}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleDone}
              disabled={selectedIds.size === 0}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
